/**
 * The pricing sections render on two surfaces: the dark public marketing page
 * and the light dashboard. One class map per tone keeps the sections
 * themselves free of conditionals.
 */
export type PricingTone = "dark" | "light";

export interface PricingTheme {
    section: string;
    card: string;
    heading: string;
    subheading: string;
    body: string;
    muted: string;
    accent: string;
    accentBg: string;
    divider: string;
    tableHead: string;
    input: string;
    link: string;
    pill: string;
}

const THEMES: Record<PricingTone, PricingTheme> = {
    dark: {
        section: "rounded-2xl border border-slate-700 bg-slate-800/60 p-6 sm:p-8",
        card: "rounded-xl border border-slate-700 bg-slate-900/60 p-5",
        heading: "text-white",
        subheading: "text-slate-100",
        body: "text-slate-300",
        muted: "text-slate-400",
        accent: "text-orange-400",
        accentBg: "bg-orange-500/15 text-orange-300 border border-orange-500/30",
        divider: "divide-slate-700 border-slate-700",
        tableHead: "text-slate-400",
        input: "bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:border-orange-500 focus:ring-orange-500",
        link: "text-orange-400 hover:text-orange-300",
        pill: "bg-slate-700 text-slate-200",
    },
    light: {
        section: "rounded-panel border border-brand-line bg-white p-6 shadow-card",
        card: "rounded-card border border-brand-line bg-brand-cream/30 p-5",
        heading: "text-brand-ink",
        subheading: "text-brand-ink",
        body: "text-brand-ink/70",
        muted: "text-brand-ink/50",
        accent: "text-brand-orange",
        accentBg: "bg-brand-orange/10 text-brand-orange border border-brand-orange/30",
        divider: "divide-brand-line border-brand-line",
        tableHead: "text-brand-ink/60",
        input: "bg-white border border-brand-line text-brand-ink placeholder-brand-ink/40 focus:border-brand-orange focus:ring-brand-orange",
        link: "text-brand-orange hover:text-brand-orange/80",
        pill: "bg-brand-cream text-brand-ink/70",
    },
};

export const pricingTheme = (tone: PricingTone): PricingTheme => THEMES[tone];
